import React, { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { BasicTable } from '../../components/basic-table';
import { atcQueryKeys } from './query-keys';
import { getSuccessRateColor } from './utils/health-colors';

interface StatusSummaryTableProps {
  timeInterval: TimeInterval;
}

export const StatusSummaryTable: React.FC<StatusSummaryTableProps> = ({
  timeInterval,
}) => {
  const client = useUntenantedClient();

  const { data } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getStatusSummary({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.statusSummary(timeInterval),
  });

  const columns = useMemo<ColumnDef<(typeof data)[number]>[]>(
    () => [
      {
        accessorKey: 'service',
        header: 'Service',
        cell: (info) => (
          <span className="font-mono text-xs text-gray-700">
            {info.getValue() as string}
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: (info) => (
          <span className="font-medium">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
      {
        accessorKey: 'successCount',
        header: 'Success',
        cell: (info) => info.getValue() as number,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'failureCount',
        header: 'Failures',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-red-600">{value}</span>
          ) : (
            <span>{value}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: 'percentSuccess',
        header: '% Success',
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <span className={`font-semibold ${getSuccessRateColor(value)}`}>
              {value.toFixed(2)}%
            </span>
          );
        },
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-700">
        Job Status Summary
      </h3>
      <BasicTable
        data={data}
        columns={columns}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={false}
      />
    </div>
  );
};
